import { GA_ACTION, GA_CATEGORIES, PAGE_URLS } from "./constants";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import React, { useEffect } from "react";
import { setUserPropertiesGa, triggerGa } from "./gaUtils";
import { useDispatch, useSelector } from "react-redux";
import {
  useLazyGetSessionIdQuery,
  useUpdateAppUtmMutation,
  useHandlePlotlineEventsDataMutation,
} from "../redux/reduxAPI";

import FooterContainer from "../components/FooterContainer";
import LoadingPage from "../components/Fallback/LoadingPage";
import { SupportWidget } from "../components/SupportWidget/SupportWidget";
import { validateRouteAccess } from "./Router/router.utils";
import { buildUserDataWithFTUX } from "./ftuxHelper";
import { getPlatform } from "./config";
import { triggerGAUserId, getPlatformFromStorage } from "./common";
import { updateCreatorLoggedIn } from "../redux/redux";
import { useBlitzllama } from "../hooks/Blitzllama/useBlitzllama";
import useClarity from "hooks/useClarity";
import { useGetCreatorConfigQuery } from "../redux/efficientReduxAPI";
import { useSingular } from "../hooks/useSingular";
import { useUtmTracker } from "hooks/useUtmTracker";
import InstaLoginInfoDrawer from "components/Home/InstaLoginInfoDrawer";
import usePlotline from "hooks/usePlotline";
import { useAuthReady } from "../FirebaseWrapper";
import { auth } from "../utils/firebase";

const CustomRedirect = ({ to, redirectingMessage }) => {
  const { pageName, screenName } = useSelector((state) => state.appReducer);
  useEffect(() => {
    if (redirectingMessage) {
      triggerGa({
        category: GA_CATEGORIES.NAVIGATION,
        action: GA_ACTION.REDIRECT,
        label: to,
        label2: pageName,
        custom: screenName,
      });
    }
  }, []);
  return <Navigate to={to} replace={true} />;
};

const RenderRoutes = (props) => {
  const { routes } = props;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { authInProgress } = useAuthReady();
  const { firebaseLoginIsLoading } = useSelector(
    (state) => state.apiDataReducer
  );

  // Check if admin user (admins don't need Firebase auth)
  const isAdminUser = Boolean(localStorage.getItem("adminToken"));

  // Skip API call if auth state change is in progress (but allow admin users)
  const shouldSkipApi = authInProgress && !isAdminUser;

  const {
    data: creatorConfig,
    isLoading: isCreatorConfigLoading,
    error: creatorConfigError,
    isError: creatorConfigIsError,
  } = useGetCreatorConfigQuery(undefined, {
    skip: shouldSkipApi,
  });

  const [triggerGetSessionId, resultGetSessionId] = useLazyGetSessionIdQuery();
  const [triggerUpdateAppUtm] = useUpdateAppUtmMutation();

  const { setBlitzllamaUserId } = useBlitzllama();
  const { setUserIdInSingular } = useSingular();
  const dispatch = useDispatch();
  const { setUserIdClarity, setUserPropertiesClarity } = useClarity();
  const { initPlotline, setUserPropertiesPlotline } = usePlotline();

  // Get Firebase login success completion flag from Redux
  const firebaseLoginSuccessCompleted = useSelector(
    (state) => state.appReducer.firebaseLoginSuccessCompleted
  );
  const [triggerHandlePlotlineEventsData] =
    useHandlePlotlineEventsDataMutation();
  useUtmTracker();

  const handlePlotlineEventsData = (eventName, properties) => {
    properties = { ...properties, eventName: eventName };
    triggerHandlePlotlineEventsData(properties);
  };

  useEffect(() => {
    const redirectFirebaseToken = searchParams?.get("ft");
    const redirectApp = searchParams?.get("app");
    if (redirectFirebaseToken) {
      localStorage.setItem("firebaseToken", redirectFirebaseToken);
      localStorage.setItem("loginApp", redirectApp);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (creatorConfig?.creator_id) {
      triggerGAUserId(creatorConfig.creator_id);
      try {
        setBlitzllamaUserId(String(creatorConfig.creator_id));
      } catch (e) {
        console.error("Error setting Blitzllama user ID:", e);
      }
      if (creatorConfig?.phone_number) {
        setUserIdInSingular(String(creatorConfig?.phone_number));
      }
      setUserIdClarity(String(creatorConfig.creator_id));
      const authorizations = creatorConfig.authorizations;
      const first_post = !!authorizations?.FIRST_POST?.achieved;
      const social_login_complete = !!authorizations?.SOCIAL_LOGIN?.achieved;
      const first_collection = !!authorizations?.FIRST_COLLECTION?.achieved;
      const creator_profile = !!authorizations?.PROFILE_COMPLETED?.achieved;
      const first_sale = !!authorizations?.FIRST_SALE?.achieved;
      const earnings_cross_2k = !!authorizations?.EARNINGS_CROSS_2K?.achieved;
      const engage_connected = !!authorizations?.ENGAGE_CONNECTED?.achieved;
      const first_link_created = !!authorizations?.FIRST_LINK_CREATED?.achieved;
      const sourcing_access = !!authorizations?.is_sourcing_enabled?.achieved;
      const is_engage_active = !!authorizations?.is_engage_active?.achieved;
      const is_boi_flipkart = creatorConfig.is_boi_flipkart;

      const account_type = creatorConfig?.account_type;
      const creator_onboarded = creatorConfig?.csm_details?.creator_onboarded;
      const csm_cohort = creatorConfig?.csm_details?.cohort;
      const csm_sub_cohort = creatorConfig?.csm_details?.sub_cohort;
      const creator_username = creatorConfig?.authenticated_creator_username;
      const creator_id = creatorConfig?.creator_id;

      // Added Firebase ID in user properties for GA
      const firebase_id = creatorConfig?.firebase_id;

      const milestoneProperties = {
        first_post,
        social_login_complete,
        first_collection,
        creator_profile,
        first_sale,
        earnings_cross_2k,
        engage_connected,
        first_link_created,
      };
      const cohortProperties = creator_onboarded
        ? { csm_cohort, csm_sub_cohort }
        : {};

      const userProperties = {
        is_boi_flipkart,
        account_type,
        creator_onboarded,
        creator_username,
        sourcing_access,
        is_engage_active,
        firebase_id,
        ...cohortProperties,
        ...milestoneProperties,
      };

      setUserPropertiesGa(userProperties);
      setUserPropertiesClarity(userProperties);
      if (creatorConfig?.phone_number && social_login_complete) {
        initPlotline(
          String(creatorConfig?.phone_number),
          handlePlotlineEventsData
        );
        setUserPropertiesPlotline({ ...userProperties, creator_id });
      }

      const fetchSessionId = async () => {
        const firebaseToken = await auth?.currentUser?.getIdToken();
        if (firebaseToken) {
          let platform = getPlatformFromStorage();
          triggerGetSessionId({
            sessionId: localStorage.getItem("sessionId") || "",
            platform: platform || "",
          });
        }
      };
      fetchSessionId();

      let appUtmSource = localStorage.getItem("appUtmSource") || "";
      let appUtmMedium = localStorage.getItem("appUtmMedium") || "";
      let appType = getPlatform();
      if (appUtmSource || appUtmMedium) {
        triggerUpdateAppUtm({
          creatorUtmSource: appUtmSource,
          creatorUtmMedium: appUtmMedium,
          creatorAppType: appType,
        });
        if (localStorage?.firebaseToken) {
          localStorage.removeItem("appUtmSource");
          localStorage.removeItem("appUtmMedium");
        }
      }
    }
  }, [creatorConfig]);

  useEffect(() => {
    const fetchSessionId = async () => {
      const firebaseToken = await auth?.currentUser?.getIdToken();
      if (firebaseToken && firebaseLoginSuccessCompleted) {
        let platform = getPlatformFromStorage();
        triggerGetSessionId({
          sessionId: localStorage.getItem("sessionId") || "",
          platform: platform || "",
        });
      }
    };
    fetchSessionId();
  }, [firebaseLoginSuccessCompleted]);

  useEffect(() => {
    if (
      resultGetSessionId.status === "fulfilled" &&
      resultGetSessionId?.data?.success
    ) {
      localStorage.setItem("sessionId", resultGetSessionId?.data?.sessionId);
    }
  }, [resultGetSessionId]);

  const getRouteElement = ({ route, userData, creatorConfigError }) => {
    // data is the mutable copy of userData. We need this as we are modifying the userData in case of creatorConfigError
    let data = userData;

    // Handle API errors - replace with empty data structure
    if (
      creatorConfigError?.status === 403 ||
      creatorConfigError?.status === 500
    ) {
      data = {
        auth_level: "",
        authorizations: {},
      };
      dispatch(updateCreatorLoggedIn(false));
    } else {
      dispatch(updateCreatorLoggedIn(true));
    }

    // this is super important, if the creator is blocked, we should not allow them to access any page
    if (data?.is_creator_blocked === true) {
      return <CustomRedirect to={PAGE_URLS.USER_BLOCKED} />;
    }

    // validate the route access
    const { isAllowed, fallbackUrl, redirectingMessage } = validateRouteAccess({
      userData: data,
      route,
      customFallback: route.customFallback,
    });

    // In case of route is not allowed by authentication or authorization redirect to fallback url
    if (!isAllowed) {
      const redirectUrl = fallbackUrl || PAGE_URLS.FIREBASELOGIN;
      return (
        <CustomRedirect
          to={redirectUrl}
          redirectingMessage={redirectingMessage}
        />
      );
    }

    // In case of route is permanently moved to another route redirect to that route
    if (route?.permanentlyMovedTo) {
      const redirectUrl = route?.permanentlyMovedTo || PAGE_URLS.FIREBASELOGIN;
      const redirectingMessage =
        "Redirecting to new route, as the route has been permanently moved";
      return (
        <CustomRedirect
          to={redirectUrl}
          redirectingMessage={redirectingMessage}
        />
      );
    }

    let RenderedComponent;
    if (typeof route.component === "function") {
      // Dynamic component - call resolver with userData
      RenderedComponent = route.component(userData);
    } else {
      // Static component - use directly
      RenderedComponent = route.component;
    }
    return (
      <>
        <RenderedComponent />
        {route.showFooter && !creatorConfigError && <FooterContainer />}
      </>
    );
  };

  return (
    <>
      {/* Show loading if auth not ready for non-admin users */}
      {shouldSkipApi || firebaseLoginIsLoading ? (
        <LoadingPage />
      ) : (
        <>
          {!!creatorConfigError || isCreatorConfigLoading === false ? (
            <>
              <Routes>
                {routes.map((route, idx) => (
                  <Route
                    key={idx}
                    path={route.path}
                    element={getRouteElement({
                      route,
                      userData: buildUserDataWithFTUX(creatorConfig),
                      creatorConfigError,
                    })}
                  />
                ))}
              </Routes>
              {creatorConfigIsError === false && (
                <>
                  <SupportWidget />
                  <InstaLoginInfoDrawer />
                </>
              )}
            </>
          ) : (
            <LoadingPage />
          )}
        </>
      )}
    </>
  );
};

export default RenderRoutes;
